import { applyAction } from '../game/engine';
import { requestId } from './client';
import type { GameAction, GameState } from '../game/types';
import { ONLINE_RULES_VERSION, projectSnapshot, type OnlineSnapshot } from './protocol';

export type ConnectionState='connecting'|'connected'|'reconnecting'|'closed';
/** Client delivery context, never supplied by or added to the wire protocol. */
export type MatchDelivery='commit'|'resume'|'sync';
type MatchListener=(snapshot:OnlineSnapshot,delivery?:MatchDelivery)=>void;
export interface MatchSession {
  readonly kind:'local'|'online';
  readonly snapshot:OnlineSnapshot|null;
  subscribe(listener:MatchListener):()=>void;
  submit(action:GameAction):Promise<void>;
  reconnect():void;
  close():void;
}

export class LocalMatchSession implements MatchSession {
  readonly kind='local' as const;
  private listeners=new Set<MatchListener>();
  snapshot:OnlineSnapshot;
  constructor(private game:GameState,private readonly persist:(s:GameState)=>Promise<void>=async()=>{}){
    this.snapshot=projectSnapshot({state:game,seat:game.humanPlayer,matchId:'offline',mode:'casual'});
  }
  get offlineState():GameState{return this.game;}
  subscribe(fn:MatchListener){this.listeners.add(fn);fn(this.snapshot,'resume');return()=>this.listeners.delete(fn);}
  async submit(action:GameAction){
    const result=applyAction(this.game,action);this.game=result.state;
    await this.persist(this.game);
    this.snapshot=projectSnapshot({state:this.game,seat:this.game.humanPlayer,matchId:'offline',mode:'casual',events:result.events});
    for(const listener of this.listeners)listener(this.snapshot,'commit');
  }
  reconnect(){for(const listener of this.listeners)listener({...this.snapshot,events:[]},'resume');}
  close(){this.listeners.clear();}
}

interface Command {type:'command';requestId:string;expectedVersion:number;actionId:string;protocolVersion:1;rulesVersion:string}
interface PendingCommand {command:Command;resolve:()=>void;reject:(error:Error)=>void}
type SocketFactory=(url:string)=>WebSocket;
export class OnlineMatchSession implements MatchSession {
  readonly kind='online' as const;
  snapshot:OnlineSnapshot|null=null;
  connection:ConnectionState='connecting';
  onConnection?:(state:ConnectionState)=>void;
  onError?:(message:string)=>void;
  private listeners=new Set<MatchListener>();
  private socket:WebSocket|null=null;
  private stopped=false;
  private retryTimer:ReturnType<typeof setTimeout>|undefined;
  private heartbeatTimer:ReturnType<typeof setTimeout>|undefined;
  private pongTimer:ReturnType<typeof setTimeout>|undefined;
  private retry=0;
  private pending:PendingCommand|null=null;
  constructor(readonly matchId:string,private readonly origin=location.origin,private readonly sockets:SocketFactory=url=>new WebSocket(url)){}
  subscribe(fn:MatchListener){this.listeners.add(fn);if(this.snapshot)fn(this.snapshot,'resume');return()=>this.listeners.delete(fn);}
  reconnect(){
    if(this.stopped)return;
    clearTimeout(this.retryTimer);
    clearTimeout(this.heartbeatTimer);clearTimeout(this.pongTimer);
    if(this.socket){this.socket.onclose=null;this.socket.close();}
    this.setConnection(this.snapshot?'reconnecting':'connecting');
    const url=new URL(`/api/matches/${encodeURIComponent(this.matchId)}/socket`,this.origin);
    url.protocol=url.protocol==='https:'?'wss:':'ws:';
    const socket=this.sockets(url.href);this.socket=socket;
    let firstSnapshot=true;
    socket.onopen=()=>{if(this.socket!==socket)return;this.retry=0;this.setConnection('connected');this.heartbeat(socket);};
    socket.onmessage=e=>{
      if(this.socket!==socket)return;
      try{
        const message=JSON.parse(String(e.data));
        if(message.type==='pong'){clearTimeout(this.pongTimer);this.heartbeat(socket);}
        else if(message.type==='state'){
          const incoming=message.snapshot as OnlineSnapshot;
          if(!validSnapshot(incoming,this.matchId)){this.onError?.('This game version is incompatible with the server. Reload to update.');this.close();return;}
          if(this.snapshot&&incoming.version<this.snapshot.version)return;
          const previous=this.snapshot;this.snapshot=incoming;
          const delivery:MatchDelivery=firstSnapshot||!previous?'resume':incoming.version>previous.version?'commit':'sync';
          // A reconnect starts at the committed board, without replaying animations.
          if(firstSnapshot||!previous||incoming.version===previous.version)incoming.events=[];
          for(const listener of this.listeners)listener(incoming,delivery);
          if(this.pending){
            if(incoming.version>this.pending.command.expectedVersion||incoming.status!=='active'){this.pending.resolve();this.pending=null;}
            else if(firstSnapshot){socket.send(JSON.stringify(this.pending.command));}
          }
          firstSnapshot=false;
        }else if(message.type==='error'){
          const text=typeof message.message==='string'?message.message:message.error?.message??'That action could not be completed.';
          if(!message.requestId||message.requestId===this.pending?.command.requestId){this.pending?.reject(new Error(text));this.pending=null;}this.onError?.(text);
        }else if(message.type==='ack'&&this.pending&&this.pending.command.requestId===message.requestId){this.pending.resolve();this.pending=null;}
      }catch{this.onError?.('The server response could not be read. Reconnecting…');socket.close();}
    };
    socket.onerror=()=>socket.close();
    socket.onclose=event=>{
      if(this.stopped||this.socket!==socket)return;
      clearTimeout(this.heartbeatTimer);clearTimeout(this.pongTimer);
      if([4001,4401,4403,1008].includes(event.code)){this.onError?.('Sign in again to resume this match.');this.close();return;}
      this.setConnection('reconnecting');
      this.retryTimer=setTimeout(()=>this.reconnect(),Math.min(15000,600*2**this.retry++));
    };
  }
  submit(action:GameAction):Promise<void>{
    if(!this.snapshot||this.connection!=='connected'||this.socket?.readyState!==1)return Promise.reject(new Error('Reconnect before submitting an action.'));
    if(this.pending)return Promise.reject(new Error('Your previous action is still being saved.'));
    const current=this.snapshot;
    if(!current.actions.some(a=>a.id===action.id&&a.player===current.seat))return Promise.reject(new Error('That action is no longer available.'));
    const command:Command={type:'command',requestId:requestId(),expectedVersion:current.version,actionId:action.id,protocolVersion:1,rulesVersion:current.rulesVersion};
    return new Promise((resolve,reject)=>{this.pending={command,resolve,reject};this.socket!.send(JSON.stringify(command));});
  }
  private setConnection(state:ConnectionState){this.connection=state;this.onConnection?.(state);}
  private heartbeat(socket:WebSocket){clearTimeout(this.heartbeatTimer);this.heartbeatTimer=setTimeout(()=>{if(this.socket!==socket||this.stopped)return;if(socket.readyState!==1)return;socket.send(JSON.stringify({type:'ping'}));this.pongTimer=setTimeout(()=>socket.close(),10000);},20000);}
  close(){this.stopped=true;clearTimeout(this.retryTimer);clearTimeout(this.heartbeatTimer);clearTimeout(this.pongTimer);if(this.socket){this.socket.onclose=null;this.socket.close();}this.socket=null;this.pending?.reject(new Error('Match connection closed.'));this.pending=null;this.listeners.clear();this.setConnection('closed');}
}
function validSnapshot(value:OnlineSnapshot,matchId:string):boolean{
  return !!value&&value.protocolVersion===1&&value.rulesVersion===ONLINE_RULES_VERSION&&value.matchId===matchId&&Number.isSafeInteger(value.version)
    &&(value.seat===0||value.seat===1)&&Array.isArray(value.actions)&&Array.isArray(value.events)&&Array.isArray(value.log)
    &&!!value.state&&value.state.humanPlayer===value.seat&&value.state.players?.length===2;
}
